'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Search, X } from 'lucide-react'
import { usePlayerSearch } from '@/lib/api/player-routes'
import { useTeamSearch } from '@/lib/api/team-routes'
import { Spinner } from '@/components/ui/spinner'
import { abbreviatePosition } from '@/lib/utils'

const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 250

export function NavSearchBar() {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  const isSearchable = debouncedQuery.length >= MIN_QUERY_LENGTH

  const { data: teamData, isFetching: teamsLoading } = useTeamSearch({ q: debouncedQuery, limit: 8 })
  const { data: playerData, isFetching: playersLoading } = usePlayerSearch({ q: debouncedQuery, limit: 8 })
  const teams = isSearchable ? teamData?.data ?? [] : []
  const players = isSearchable ? playerData?.data ?? [] : []
  const loading = teamsLoading || playersLoading
  const hasResults = teams.length > 0 || players.length > 0
  const showPanel = focused && isSearchable

  function clear() {
    setQuery('')
    setDebouncedQuery('')
  }

  return (
    <div className="relative w-full sm:max-w-xs">
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-[#0a0c0e] px-3 py-1.5">
        <Search className="size-4 shrink-0 text-white/40" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder="Search players or teams… (3+ chars)"
          className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/35"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear search"
            className="flex size-5 shrink-0 items-center justify-center text-white/40 transition-colors hover:text-white"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {showPanel && (
        // Fixed + left-1/2/-translate-x-1/2 centers this on the viewport
        // rather than under the input itself — the input can be off-centre
        // (e.g. next to the logo) but the results should always appear in
        // the horizontal middle of the screen.
        <div className="fixed left-1/2 top-24 z-50 max-h-80 w-[min(92vw,26rem)] -translate-x-1/2 animate-in overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-[#0e1114] shadow-xl fade-in-0 slide-in-from-top-1 duration-150 sm:top-28">
          {loading && !hasResults && (
            <div className="flex justify-center py-3">
              <Spinner className="size-4 text-white/40" />
            </div>
          )}
          {!loading && !hasResults && <p className="px-3 py-2.5 text-sm text-white/40">No results found.</p>}

          {teams.map((team, i) => (
            <Link
              key={`team-${team.team_id}`}
              href={`/teams/${team.team_id}`}
              onClick={clear}
              style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
              className="flex w-full animate-in items-center justify-between gap-3 border-b border-white/10 px-3 py-2 text-left no-underline fade-in-0 slide-in-from-bottom-1 transition-colors duration-200 ease-out last:border-b-0 hover:bg-white/5"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center">
                  {team.logo_url && <img src={team.logo_url} alt="" className="size-6 object-contain" />}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm text-white">
                  {team.common_name || team.team_name}
                </span>
              </span>
              {team.country?.flag_url && (
                <img src={team.country.flag_url} alt="" className="h-3.5 w-5 shrink-0 object-cover" />
              )}
            </Link>
          ))}

          {players.map((p, i) => (
            <Link
              key={`player-${p.player_id}`}
              href={`/players/${p.player_id}`}
              onClick={clear}
              style={{ animationDelay: `${(teams.length + i) * 30}ms`, animationFillMode: 'backwards' }}
              className="flex w-full animate-in items-center gap-3 border-b border-white/10 px-3 py-2 text-left no-underline fade-in-0 slide-in-from-bottom-1 transition-colors duration-200 ease-out last:border-b-0 hover:bg-white/5"
            >
              <span className="flex size-8 shrink-0 items-center justify-center">
                {p.curr_team?.logo_url && (
                  <img src={p.curr_team.logo_url} alt="" className="size-6 object-contain" />
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm text-white">{p.player_name}</span>
                <span className="flex items-center gap-1 truncate text-xs text-white/40">
                  {p.countries.country1?.flag_url && (
                    <img src={p.countries.country1.flag_url} alt="" className="h-2.5 w-3.5 shrink-0 object-cover" />
                  )}
                  {p.position && <span className="truncate">{abbreviatePosition(p.position)}</span>}
                </span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
